import { z } from 'zod';
import { useSsoStepUp } from '@/api/mutations/useAuth';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { isReauthRequiredError } from '@/api/errors';
import { useBrowserRedirect } from '@/lib/useBrowserRedirect';

/**
 * What the user was doing when the step-up interrupted them, so SecurityTab
 * can re-open the exact affordance after the re-auth round trip. Destructive
 * kinds only re-open their ConfirmDialog on return — they never auto-fire the
 * mutation, because persisted state or a crafted ?settings link must not
 * delete a credential without a fresh confirm click.
 */
const PendingSensitiveActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('totp-register') }),
  z.object({ kind: z.literal('totp-remove') }),
  z.object({ kind: z.literal('passkey-remove'), passkeyId: z.string() })
]);

export type PendingSensitiveAction = z.infer<typeof PendingSensitiveActionSchema>;

/**
 * Everything the step-up dialog can be opened for: a resumable marker, or the
 * passkey-add flow, which deliberately has no marker (a single "Add passkey"
 * click re-mints a fresh link after the reload; auto-window.open would be
 * popup-blocked).
 */
export type StepUpAction = PendingSensitiveAction | 'passkey-add';

/**
 * AUTH_REAUTH_REQUIRED (stale-session 403) routes to this callback — the
 * consumer opens the step-up dialog. Every other error (AUTH_NO_MEMBERSHIP
 * included) keeps the toast path; the two never both fire.
 */
export interface SensitiveMutationOptions {
  onReauthRequired: (pendingAction: PendingSensitiveAction) => void;
}

/** Shared onError classification for the sensitive factor mutations. */
export function handleSensitiveMutationError(
  error: unknown,
  toastKey: string,
  onReauthRequired: () => void
): void {
  if (isReauthRequiredError(error)) {
    onReauthRequired();
    return;
  }
  toastApiError(error, toastKey);
}

const PENDING_ACTION_KEY = 'bliks.pending-sensitive-action';

// All storage access is throw-safe: a browser that denies web storage
// (privacy mode, embedded context) degrades to "no resume marker" instead of
// crashing the security dialog or the step-up start.
export function storePendingSensitiveAction(action: PendingSensitiveAction): void {
  try {
    sessionStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
  } catch {
    // Step-up proceeds without a resume marker.
  }
}

/**
 * Clears the marker. Split from peek (instead of a take-once) so consumers can
 * read during render and clear in a mount effect — a discarded render must not
 * consume the marker.
 */
export function clearPendingSensitiveAction(): void {
  try {
    sessionStorage.removeItem(PENDING_ACTION_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Reads without removing; a crafted value must never throw during render. */
export function peekPendingSensitiveAction(): PendingSensitiveAction | null {
  try {
    const raw = sessionStorage.getItem(PENDING_ACTION_KEY);
    if (raw === null) return null;
    const result = PendingSensitiveActionSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

/**
 * Step-up re-authentication for sensitive-operation 403s
 * (AUTH_REAUTH_REQUIRED). `beginStepUp` — always behind a user gesture, the
 * SecurityTab dialog's confirm — starts a full-redirect re-auth at Zitadel
 * and returns to the security settings via the SSO callback's `returnTo`.
 *
 * Deliberately no router hooks: window.location.pathname is router-identical
 * (router.ts sets no basepath), so consumers need no router context. No
 * splash either — the redirect leaves the page intact until unload;
 * unlike workspace switch there is no cache teardown to hide.
 */
export function useStepUpReauth() {
  const stepUp = useSsoStepUp();
  const { isRedirecting, redirect, reset } = useBrowserRedirect();

  const beginStepUp = async (pendingAction?: PendingSensitiveAction): Promise<void> => {
    if (pendingAction) storePendingSensitiveAction(pendingAction);
    try {
      const { authorizationUrl } = await stepUp.mutateAsync({
        returnTo: `${window.location.pathname}?settings=security`
      });
      redirect(authorizationUrl);
    } catch (error: unknown) {
      // A failed start must not leave a marker behind — a later unrelated
      // visit to the security tab would otherwise replay the stale action.
      clearPendingSensitiveAction();
      toastApiError(error, 'errors.auth.reauthRequired');
    }
  };

  return {
    beginStepUp,
    isStarting: stepUp.isPending || isRedirecting,
    // Only the start request may hold the dialog open: a redirect the browser
    // cancels never reports back, so it must stay dismissable.
    isRequesting: stepUp.isPending,
    resetRedirect: reset
  };
}
